import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { Toaster } from './components/ui/sonner';

// Pages
import LoginPage from './pages/LoginPage';
import AuthCallback from './pages/AuthCallback';
import DashboardPage from './pages/DashboardPage';
import BrandDashboardPage from './pages/BrandDashboardPage';
import ChannelDashboardPage from './pages/ChannelDashboardPage';
import ProductDashboardPage from './pages/ProductDashboardPage';
import ReviewsPage from './pages/ReviewsPage';
import SentimentPage from './pages/SentimentPage';
import ProductInsightsPage from './pages/ProductInsightsPage';
import PlatformInsightsPage from './pages/PlatformInsightsPage';
import CustomerVoicePage from './pages/CustomerVoicePage';
import AlertsPage from './pages/AlertsPage';
import ReportsPage from './pages/ReportsPage';
import SettingsPage from './pages/SettingsPage';
import UploadReviewsPage from './pages/UploadReviewsPage';
import CustomerSupportPage from './pages/CustomerSupportPage';

// Layout
import AppLayout from './layouts/AppLayout';

// Protected Route Component
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  // If user data is passed from AuthCallback, skip auth check
  if (location.state?.user) {
    return children;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#2eb4ac] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

// App Router with OAuth callback handling
function AppRouter() {
  const location = useLocation();

  // CRITICAL: Check URL fragment for session_id synchronously during render
  // This prevents race conditions by processing new session_id FIRST
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="brand/:brandName" element={<BrandDashboardPage />} />
        <Route path="brand/:brandName/channel/:platformName" element={<ChannelDashboardPage />} />
        <Route path="brand/:brandName/channel/:platformName/product/:productName" element={<ProductDashboardPage />} />
        <Route path="reviews" element={<ReviewsPage />} />
        <Route path="sentiment" element={<SentimentPage />} />
        <Route path="products" element={<ProductInsightsPage />} />
        <Route path="platforms" element={<PlatformInsightsPage />} />
        <Route path="customer-voice" element={<CustomerVoicePage />} />
        <Route path="alerts" element={<AlertsPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="upload-reviews" element={<UploadReviewsPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
